import {
  type AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import dayjs from 'dayjs';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { placeMidWeight } from '../../../server/types/task-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import { DragService, type TaskDragData } from '../drag.service';
import {
  type CompletedListFilter,
  type ListFilter,
  type OrgTaskWithUpdates,
  type TaskItem,
  taskMatch,
  TaskService,
} from '../task.service';
import { UtilsService } from '../utils.service';

const standardTaskHeight = '75px';
const recentlyCompletedBufferPeriod = 'day';

type TaskListTaskItem = TaskItem & {
  taskContainerId: string;
  visible: boolean;
  height: string;
  observed: boolean;
  scrollSubscription?: Subscription;
};

@Component({
  selector: 'app-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss'],
  standalone: false,
})
export class TaskListComponent implements OnInit, AfterViewInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private utilsService = inject(UtilsService);
  private taskService = inject(TaskService);
  public dragService = inject(DragService);

  @Input() readonly orgId: string;
  @Input() readonly spaceId: string;
  @Input() readonly listId: string;
  @Input() set listFilter({
    listStage,
    assignedTo,
    tagIds,
    completedAtRange,
  }: ListFilter) {
    if (listStage === 'backlog' || listStage === 'current')
      this.listFilterInternal = {
        listStage,
        assignedTo,
        tagIds,
        recentlyCompletedBuffer: dayjs()
          .startOf(recentlyCompletedBufferPeriod)
          .valueOf(),
      };
    else if (listStage === 'completed' && completedAtRange)
      this.listFilterInternal = {
        listStage,
        assignedTo,
        tagIds,
        completedAtRange,
      };
    else
      this.listFilterInternal = {
        listStage: 'trash',
        assignedTo,
        tagIds,
      };
  }
  @Input() readonly headerText: string;
  @Input() readonly selectFirstInListTaskPathBase?: string; // set if you want the first task selected in the list
  @Input() preventDragAndEdit?: boolean;

  @ViewChild('listScroller') listScroller: ElementRef<HTMLTextAreaElement>;

  authUserId: string;

  headerLink = '';

  taskItems: TaskListTaskItem[] = [];

  listFilterInternal: ListFilter;

  countCompletedTasks: TaskItem[] = [];
  countCompletedTaskFilter?: ListFilter;

  moveCursorToDeepestChildTaskId: string | undefined;
  moveCursorToDeepestChildAtStart = false;

  initialLoadCompleted = false;

  dragHighlight = false;

  showRecentlyCompleted = true;
  allowDisplayOfRecentlyCompleted = true;

  showMoreAbove = false;
  showMoreBelow = false;

  subscriptions = new Subscription();

  intersectionObserver: IntersectionObserver;

  ngOnInit(): void {
    const userOrgObj = this.authUserService.getAuthUserOrgObj({});
    this.authUserId = userOrgObj.user._id;
    const space = userOrgObj.org.spaces.find((t) => t._id === this.spaceId);
    if (!space) throw new Error('spaceId not found');

    if (!this.listId) throw new Error('listId not provided');

    this.allowDisplayOfRecentlyCompleted =
      this.listFilterInternal.listStage === 'backlog' ||
      this.listFilterInternal.listStage === 'current';

    // Get the tasks stored
    this.updateTasks(
      this.taskService.getStoredTasks({
        spaceId: this.spaceId,
        listFilter: this.listFilterInternal,
      })
    );

    // Immediately show the first 30 to increase load speed
    let i = 0;
    while (i < 30 && this.taskItems[i]) {
      this.taskItems[i].visible = true;
      i++;
    }

    // Watch for new tasks and update accordingly
    const updateSubscription = this.taskService
      .spaceTaskUpdateFeed({ spaceId: this.spaceId })
      .subscribe({ next: (tasks) => this.updateTasks(tasks) });
    this.subscriptions.add(updateSubscription);

    // Count completed tasks
    if (this.listFilterInternal.listStage === 'current') {
      const completedListFilter: CompletedListFilter = _.cloneDeep({
        ...this.listFilterInternal,
        completedAtRange: { start: this.utilsService.getStartOfWeek() }, // Set the range to this week
        listStage: 'completed',
      });

      this.countCompletedTaskFilter = completedListFilter;

      this.countCompletedTasks = this.taskService
        .getStoredTasks({
          spaceId: this.spaceId,
          listFilter: completedListFilter,
        })
        .map((t) => ({ taskId: t._id, task: t }));

      // Watch for new completed tasks and update accordingly
      const completedSubscription = this.taskService
        .spaceTaskUpdateFeed({ spaceId: this.spaceId })
        .subscribe({
          next: (tasks) => {
            tasks.forEach((task) => {
              const index = this.countCompletedTasks.findIndex(
                (t) => t.taskId === task._id
              );

              if (!this.countCompletedTaskFilter) {
                console.error('expected recentlyCompletedTaskFilter to be set');
                return;
              }

              // #indexCheck-taskListComponent
              if (index === -1) {
                if (taskMatch(task, this.countCompletedTaskFilter)) {
                  // Add if matching
                  this.countCompletedTasks.push({
                    taskId: task._id,
                    task,
                  });
                }
              } else {
                if (!taskMatch(task, this.countCompletedTaskFilter)) {
                  // Remove if no longer matching
                  this.countCompletedTasks.splice(index, 1); // #indexCheck-taskListComponent
                }
              }
            });
          },
        });
      this.subscriptions.add(completedSubscription);
    }

    // Remove tasks (used in removing blank tasks)
    const removeSubscription = this.taskService
      .taskRemoves({ orgId: this.orgId, spaceId: this.spaceId })
      .subscribe(({ taskId: taskId1 }) => {
        const index = this.taskItems.findIndex((t) => t.taskId === taskId1);
        if (index !== -1) {
          this.taskItems[index].scrollSubscription?.unsubscribe();
          this.deselectTask(taskId1);
          this.taskItems.splice(index, 1);
        }
      });
    this.subscriptions.add(removeSubscription);
  }

  ngAfterViewInit(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const taskId = entry.target.id.split('-')[1];
          const taskItem = this.taskItems.find((t) => t.taskId === taskId);

          if (entry.isIntersecting) {
            if (!taskItem) {
              console.error(
                'strange state in intersection observer: taskItem not found'
              );
              return;
            }

            if (!taskItem.visible) taskItem.visible = true;
          } else {
            if (taskItem?.visible) {
              taskItem.height = entry.target.scrollHeight + 'px';
              taskItem.visible = false;
            }
          }
        });
      },
      {
        root: this.listScroller.nativeElement,
        rootMargin: '2000px',
        threshold: 0,
      }
    );

    setTimeout(() => {
      this.scrollPositionCalculate(); // Prevent ExpressionChangedAfterItHasBeenCheckedError
    }, 10);
    this.listScroller.nativeElement.addEventListener('scroll', (event) => {
      this.scrollPositionCalculate();
    });

    this.taskItems.forEach((t) => this.addObserveIntersection(t));
    setTimeout(() => (this.initialLoadCompleted = true), 5000);
  }

  private scrollPositionCalculate() {
    if (this.listScroller.nativeElement.scrollTop > 23) {
      if (!this.showMoreAbove) this.showMoreAbove = true;
    } else {
      if (this.showMoreAbove) this.showMoreAbove = false;
    }

    if (
      this.listScroller.nativeElement.scrollHeight -
        this.listScroller.nativeElement.clientHeight -
        this.listScroller.nativeElement.scrollTop >
      23
    ) {
      if (!this.showMoreBelow) this.showMoreBelow = true;
    } else {
      if (this.showMoreBelow) this.showMoreBelow = false;
    }
  }

  scrollUp() {
    this.listScroller.nativeElement.scrollBy({
      top: -(this.listScroller.nativeElement.clientHeight - 80),
      behavior: 'smooth',
    });
  }

  scrollDown() {
    this.listScroller.nativeElement.scrollBy({
      top: this.listScroller.nativeElement.clientHeight - 80,
      behavior: 'smooth',
    });
  }

  updateOrAddSingleTask(task: DeepReadonly<OrgTaskWithUpdates>) {
    // Note: this function does NOT update ordering or context depth
    const existingIndex = this.taskItems.findIndex(
      (i) => i.taskId === task._id
    );

    // Remove if it no longer matches this list
    if (existingIndex !== -1 && !this.taskFullyMatchesList(task)) {
      this.taskItems[existingIndex].scrollSubscription?.unsubscribe();
      this.deselectTask(task._id);
      this.taskItems.splice(existingIndex, 1);
      return;
    }

    // Update
    if (this.taskItems[existingIndex]) {
      this.taskItems[existingIndex].task = task;
      return;
    }

    // Add new if matching
    if (this.taskFullyMatchesList(task)) {
      const { orgId, spaceId, _id: taskId } = task;
      // Check if it has a parent.
      if (task.parentId) {
        const parentTasks = this.taskService.getParents({
          orgId,
          spaceId,
          taskId,
        });
        // If one of its parents matches the list, do not include it (bail).
        const matches = parentTasks.filter((t) => this.taskFullyMatchesList(t));
        if (matches.length > 0) return;
      }

      const visible = this.initialLoadCompleted; // Hide tasks which are present at load but off page. New tasks are always visible
      const observed = false;
      const taskContainerId = this.listId + '-' + taskId;

      const taskItem: TaskListTaskItem = {
        taskId,
        task,
        taskContainerId,
        visible,
        height: standardTaskHeight,
        observed,
      };

      const scrollSubscription = this.taskService
        .getScrollObservable({ taskId })
        .subscribe(() => {
          this.scrollToElement(taskItem);
        });
      taskItem.scrollSubscription = scrollSubscription;
      this.subscriptions.add(scrollSubscription);

      // We will roughly insert into the correct location (tasks might not
      // actually be sorted correctly, and some items might still be yet to be
      // deleted)
      let insertIndex = 0;
      const originalTasksLength = this.taskItems.length;
      let inserted = false;
      while (insertIndex < originalTasksLength && !inserted) {
        if (task.weight < this.taskItems[insertIndex].task.weight) {
          this.taskItems.splice(insertIndex, 0, taskItem);
          inserted = true;
        }
        insertIndex++;
      }
      if (!inserted) this.taskItems.push(taskItem);

      setTimeout(() => this.addObserveIntersection(taskItem), 20);
    }
  }

  private scrollToElement(taskItem: TaskListTaskItem, attempt = 0) {
    taskItem.visible = true;
    const ele = document.getElementById(taskItem.taskContainerId);
    if (ele) {
      ele.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'nearest',
      });
    } else {
      // Try again after a bit
      const time = [10, 50, 100, 500, 1000, 5000, 10000][attempt];
      attempt++;
      if (!time) {
        // could not scroll to element, might not be created yet or about to be destroyed
        return;
      }
      setTimeout(() => this.scrollToElement(taskItem, time), time);
    }
  }

  private addMatchingChildren(parentId: string) {
    const { orgId, spaceId } = this;
    const children = this.taskService.getChildren({ orgId, spaceId, parentId });
    children.forEach((task) => {
      // If it matches, we don't need to check children further. Otherwise we
      // check the children.
      if (this.taskFullyMatchesList(task)) this.updateOrAddSingleTask(task);
      else this.addMatchingChildren(task._id);
    });
  }

  updateTasks(tasks: DeepReadonly<OrgTaskWithUpdates>[]) {
    const oldTaskCount = this.taskItems.length;

    // Place all top level tasks
    // Place all fully matching subtasks with no parent currently inside existing or update list
    // Clear out non-matching top-level tasks and non-fully matching children

    // Update any existing items in the list or add them
    tasks.forEach((task) => this.updateOrAddSingleTask(task));

    // Clean out top-level tasks in the list, starting from the back to allow splice to work properly
    const removedTasks: DeepReadonly<OrgTaskWithUpdates>[] = [];
    for (let i = this.taskItems.length - 1; i >= 0; i--) {
      if (!this.taskFullyMatchesList(this.taskItems[i].task)) {
        removedTasks.push(this.taskItems[i].task);
        this.taskItems[i].scrollSubscription?.unsubscribe();
        this.deselectTask(this.taskItems[i].task._id);
        this.taskItems.splice(i, 1); // remove the task
      }
    }

    // Get children for each removed task, add any that perfectly matches the list (not unset though)
    removedTasks.forEach((task) => this.addMatchingChildren(task._id));

    // Determine if any of the child tasks actually has a parent in the list,
    // in which case we should remove it.
    for (let i = this.taskItems.length - 1; i >= 0; i--) {
      if (this.taskItems[i].task.parentId) {
        const parentTasks = this.taskService.getParents({
          orgId: this.orgId,
          spaceId: this.spaceId,
          taskId: this.taskItems[i].taskId,
        });
        // If one of its parents matches the list, do not include it (bail).
        const matches = parentTasks.filter((t) => this.taskFullyMatchesList(t));
        if (matches.length > 0) {
          this.taskItems[i].scrollSubscription?.unsubscribe();
          this.deselectTask(this.taskItems[i].task._id);
          this.taskItems.splice(i, 1); // remove the task}
        }
      }
    }

    // Sort items
    if (this.listFilterInternal.listStage === 'trash') {
      this.taskItems.sort((a, b) => {
        if (b.task.trashedAt && a.task.trashedAt) {
          return b.task.trashedAt - a.task.trashedAt;
        }
        console.error('trashedAt not set in trash stage');
        return 0;
      });
    } else {
      this.taskItems.sort((a, b) => a.task.weight - b.task.weight);
    }
    // On first load, select first task in list
    if (oldTaskCount === 0 && this.taskItems.length) {
      const firstTaskItem = this.taskItems[0];
      if (this.selectFirstInListTaskPathBase && firstTaskItem) {
        const listId = this.listId;
        const { orgId, spaceId, _id: taskId, parentId } = firstTaskItem.task;
        this.taskService.setTaskSelectionStart({
          orgId,
          spaceId,
          taskId,
          parentId,
          listId,
          orphaned: !!parentId, // If it has a parentId, it is orphaned
        });
      }
    }
  }

  private addObserveIntersection(taskItem: TaskListTaskItem, attempt = 0) {
    const ele = document.getElementById(taskItem.taskContainerId);

    if (ele) {
      this.intersectionObserver.observe(ele);
      taskItem.observed = true;
    } else {
      // Try again after a bit
      const time = [10, 50, 100, 500, 1000, 5000, 10000][attempt];
      attempt++;
      if (!time) {
        // could not observe element, might not be created yet or about to be destroyed
        return;
      }
      setTimeout(() => this.addObserveIntersection(taskItem, time), time);
    }
  }

  toggleShowRecentlyCompleted() {
    if (
      this.listFilterInternal.listStage === 'backlog' ||
      this.listFilterInternal.listStage === 'current'
    ) {
      this.showRecentlyCompleted = !this.showRecentlyCompleted;
      if (this.showRecentlyCompleted) {
        this.listFilterInternal.recentlyCompletedBuffer = dayjs()
          .startOf(recentlyCompletedBufferPeriod)
          .valueOf();
      } else {
        this.listFilterInternal.recentlyCompletedBuffer = undefined;
      }

      this.updateTasks(
        this.taskService.getStoredTasks({
          spaceId: this.spaceId,
          listFilter: this.listFilterInternal,
        })
      );
    }
  }

  private taskFullyMatchesList(task: DeepReadonly<OrgTaskWithUpdates>) {
    return taskMatch(task, this.listFilterInternal);
  }

  addNewTaskAtStart() {
    const firstTask = this.taskItems[0];
    const weight = placeMidWeight(undefined, firstTask?.task.weight);
    this.blankTask({ weight });
  }

  addNewTaskAtEnd() {
    const lastTask = this.taskItems[this.taskItems.length - 1];
    const weight = placeMidWeight(lastTask?.task.weight, undefined);
    this.blankTask({ weight });
  }

  blankTask({
    weight,
    parentId,
    parentWeight,
  }: {
    weight: number;
    parentId?: string;
    parentWeight?: number;
  }) {
    if (this.listFilterInternal.listStage === 'completed')
      throw new Error('cannot create blank test in completed list');
    const task = this.taskService.createTask({
      orgId: this.orgId,
      spaceId: this.spaceId,
      createdBy: this.authUserId,
      currentAt:
        this.listFilterInternal.listStage === 'current'
          ? Date.now()
          : undefined,
      tagIds: this.listFilterInternal.tagIds ?? [],
      assignedTo:
        this.listFilterInternal.assignedTo === null
          ? undefined
          : this.listFilterInternal.assignedTo,
      weight,
      parentId,
      parentWeight,
      autoDeletable: true,
    });
    this.taskService.setTaskCursor({ taskId: task._id, listId: this.listId });
  }

  moveCursorToPrior(atStart: boolean, priorChild?: TaskItem) {
    if (priorChild) {
      this.moveCursorToDeepestChildTaskId = undefined;
      // We setTimeout here to force an update
      setTimeout(() => {
        this.moveCursorToDeepestChildAtStart = atStart;
        this.moveCursorToDeepestChildTaskId = priorChild.taskId;
      }, 10);
    }
  }

  moveCursorToNext(atStart: boolean, nextChild?: TaskItem) {
    if (nextChild) {
      const taskId = nextChild.taskId;
      const { listId } = this;
      this.taskService.setTaskCursor({
        taskId,
        listId,
        start: atStart ? 0 : undefined,
      });
    }
  }

  dragOver(event: DragEvent) {
    event.stopImmediatePropagation();
    const taskId = this.dragEventAllowed(event);
    if (!taskId) return;
    this.dragHighlight = true;
  }

  private dragEventAllowed(event: DragEvent) {
    // Only handle in the case there are no children
    if (this.taskItems.length) return;

    const dragData = this.dragService.getData();
    if (!dragData || dragData.type !== 'task') return;
    event.preventDefault(); // Necessary to allow drop to go ahead

    if (!this.dragAllowed(dragData)) return;

    return dragData.taskId;
  }

  private dragAllowed(dragData: TaskDragData): boolean {
    // Prevent drags between orgs and spaces
    if (this.orgId !== dragData.orgId || this.spaceId !== dragData.spaceId)
      return false;

    return true;
  }

  dragLeave(event: DragEvent) {
    event.stopImmediatePropagation();
    this.dragHighlight = false;
  }

  dragDrop(event: DragEvent) {
    const dragData = this.dragService.getData();
    if (!dragData || dragData.type !== 'task') return;
    event.stopImmediatePropagation();

    this.drop(dragData);
  }

  drop(dragData: TaskDragData) {
    // Let the tasks in the taskList handle the drop
    if (!this.dragHighlight) return;
    this.dragHighlight = false;

    const time = Date.now();

    const { orgId, spaceId, taskId } = dragData;
    if (this.spaceId !== spaceId)
      throw new Error('dragging between spaces not implemented');
    const draggedTask = this.taskService.getTaskLocalSync({
      orgId,
      spaceId,
      taskId,
    });
    if (!draggedTask) throw new Error('draggedTask not found');

    const changeFields = this.taskService.applyListFilter({
      draggedTask,
      listFilter: this.listFilterInternal,
      time,
      draggedTaskFromSameList: false, // Should always be false because this list is empty
      draggedTaskContextDepth: dragData.contextDepth,
    });

    const weight = placeMidWeight(undefined, undefined);

    this.taskService.updateTask(
      { orgId, spaceId, taskId },
      { ...changeFields, weight },
      { applyUpdateToChildren: true }
    );
  }

  deselectAllChildren() {
    this.taskItems.forEach((child) => this.deselectTask(child.taskId));
  }

  deselectTask(taskId: string) {
    this.taskService.removeTaskSelection({
      listId: this.listId,
      taskId,
      parentId: undefined,
      orphaned: false,
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.intersectionObserver.disconnect();

    this.deselectAllChildren();
  }
}
